import { Grid } from '@progress/kendo-react-grid';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { GridColumn } from 'src/components/GridWrapper';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import ErrorFilterMenuCell from 'src/sections/invoice_details/lineItemsColumns/ErrorFilterMenuCell';
import DeleteConfirmation from 'src/sections/receipt_details/DeleteConfirmation';
import EditInput from 'src/sections/receipt_details/EditInput';
import GLCodeDropDownCell from 'src/sections/receipt_details/GLCodesDropDownCell';
import ItemNoteDropDownCell from 'src/sections/receipt_details/ItemNoteDropDownCell';
import POQty from 'src/sections/receipt_details/lineItemsColumns/POQty';
import CurrentDocReceived from 'src/sections/receipt_details/lineItemsColumns/QuantityReceived';
import ReceiptErrorCell from 'src/sections/receipt_details/lineItemsColumns/ReceiptErrorCell';
import VarianceQtyReceived from 'src/sections/receipt_details/lineItemsColumns/VarianceQtyReceived';

const useReceiptLineItemsColumns: (tableRef: RefObject<Grid>) => GridColumn<any>[] = (tableRef) => {
  const { isBlindReceive, isInProgress } = useReceiptDetails();
  const [gridSize, setGridSize] = useState(0);

  const columnFormatSize = useMemo(() => {
    if (isBlindReceive) {
      return gridSize * 0.089;
    } else {
      return gridSize * 0.06;
    }
  }, [isBlindReceive, gridSize]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef?.current?.element) setGridSize(tableRef.current.element.offsetWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tableRef]);

  const columns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'errors',
        title: '',
        width: columnFormatSize - 100 < 50 ? 50 : columnFormatSize - 100,
        component: ReceiptErrorCell,
        columnMenu: ErrorFilterMenuCell,
        filter: 'boolean',
        menu: isInProgress ? true : false,
      },
      ...(!isInProgress
        ? []
        : ([
            {
              field: 'selected',
              title: '',
              filter: 'text',
              width: columnFormatSize - 90 < 50 ? 50 : columnFormatSize - 90,
              format: 'boolean',
            },
          ] as GridColumn<any>[])),
      {
        field: 'complete',
        title: 'All Items Received',
        filter: 'text',
        width: columnFormatSize,
        format: '',
        menu: isInProgress ? true : false,

        dataType: 'fixed',
        specificStyle: (field: string, value: any) =>
          field === 'complete'
            ? {
                backgroundColor: value?.complete === 'N' ? 'rgb(248,203,172)' : 'rgb(170,207,144)',
              }
            : {},
      },
      {
        field: 'poQty',
        title: 'PO Qty',
        filter: 'numeric',
        width: columnFormatSize,
        format: '',
        dataType: 'number',
        menu: isInProgress ? true : false,

        filterFormat: '#',
        component: POQty,
      },
      {
        field: 'receivingReceiptLineItemId',
        title: 'receivingReceiptLineItemId',
        filter: 'numeric',
        format: '',
        dataType: 'number',
        menu: isInProgress ? true : false,
        width: '0px',
        filterFormat: '#',
      },
      {
        field: 'currentDocQty',
        title: 'Current Doc Qty',
        filter: 'numeric',
        width: columnFormatSize + 30,
        menu: false,
        component: CurrentDocReceived,
      },
      {
        field: 'previousDocQty',
        title: 'Previous Doc Qty',
        filter: 'numeric',
        width: columnFormatSize + 10,
        format: '',
        menu: isInProgress ? true : false,
        dataType: 'number',
        filterFormat: '#',
      },
      {
        field: 'varianceQty',
        title: 'Variance Qty',
        filter: 'numeric',
        width: columnFormatSize + 10,
        format: '',
        menu: isInProgress ? true : false,
        dataType: 'number',
        filterFormat: '#',
        component: VarianceQtyReceived,
      },
      {
        field: 'item',
        title: 'Item #',
        filter: 'text',
        width: columnFormatSize,
        menu: isInProgress ? true : false,
        format: '',
        dataType: 'string',
        className: 'lineItemInput',
        component: EditInput,
      },
      {
        field: 'itemDescription',
        title: 'Item Description',
        filter: 'text',
        format: '',
        dataType: 'string',
        className: 'lineItemInput',
        menu: isInProgress ? true : false,

        component: EditInput,
        width: columnFormatSize + 280,
      },
      {
        field: 'poglCodeDescription',
        title: 'GL Code',
        width: columnFormatSize + 280,
        component: GLCodeDropDownCell,
        menu: isInProgress ? true : false,

        dataType: 'string',
      },
      {
        field: 'uom',
        title: 'UOM',
        filter: 'text',
        width: columnFormatSize - 20,
        format: '',
        dataType: 'string',
        className: 'lineItemInput',
        component: EditInput,
        menu: isInProgress ? true : false,
      },
      {
        field: 'itemNoteValue',
        title: 'Item Note',
        filter: 'text',
        width: columnFormatSize + 50,
        component: ItemNoteDropDownCell,
        menu: isInProgress ? true : false,
      },
      {
        field: 'canDeleteReceiptLineItem',
        title: 'Actions',
        width: columnFormatSize + 15,
        component: DeleteConfirmation,
        menu: false,
        sortable: true,
      },
    ],
    [columnFormatSize, isInProgress]
  );

  let finalColumns = useMemo(() => {
    if (isBlindReceive) {
      return columns.filter(
        (data) =>
          !['complete', 'poQty', 'previousDocQty', 'varianceQty'].includes(data?.field || '')
      );
    }
    return columns;
  }, [columns, isBlindReceive]);

  return finalColumns;
};

export default useReceiptLineItemsColumns;
